import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { Messages } from '../../../../codegen/Localize';
import { L10n } from '../../../../commonInfra/L10n/L10n';
import { ServerSettingsUnauthenticated } from '../../../../settings/services/ServerSettingsUnauthenticated';
import { DomainFamily } from '../../../../settings/types/IServerSettingsUnauthenticated';
import { IUserSettings } from '../../../../userSettings/models/IUserSettings';
import { SiteRoleOptionsResource } from '../../../services/resources/SiteRoleOptions';
import { IServerUserInfo } from '../../models/IServerUserInfo';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';
import { SiteUserAuthSetting } from '../../serverApi/types/ISiteUser';
import { SiteUserIcon } from './SiteUserIcon';

export interface UserSpaceHeaderProps {
  readonly item: IServerUserInfo | ISiteUserInfo;
  readonly inServerArea: boolean;
  readonly siteRole: string;
  readonly userSettings: IUserSettings;
  readonly isSessionUserSelf: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSessionSiteAdmin: boolean;
  readonly isServerDomainFamilyActiveDirectory: boolean;
  readonly siteRoleOptions: SiteRoleOptionsResource;
}

export function UserSpaceHeader(props: UserSpaceHeaderProps): JSX.Element {
  const {item} = props;
  return (
    <div>
      <div className={SpaceHeaderStyles.Row} data-tb-test-id={`tb-${item.contentType}-space-header-${item.id}`}>
        { renderSpaceIcon(item, props.isSessionUserSelf) }
        <div className={SpaceHeaderStyles.Column}>
          <div className={SpaceHeaderStyles.RowAlignedCenter}>
            <h1 data-tb-test-id={SpaceHeaderTestIds.name} className={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.TitleV2 : SpaceHeaderStyles.Title}>{ item.displayName }</h1>
          </div>
          <div>
            <section
              className={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.MetadataV2 : SpaceHeaderStyles.Metadata}
              style={{ flexWrap: 'wrap' }}
            >
              <>
                <Username {...props} />
                <SiteRole {...props} />
                <LastSignedIn {...props} />
                <Email {...props} />
              </>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderSpaceIcon(item: IServerUserInfo | ISiteUserInfo, isSessionUserSelf: boolean): JSX.Element {
  return <SiteUserIcon user={item} isSessionUserSelf={isSessionUserSelf} />;
}

function Username(props: UserSpaceHeaderProps): JSX.Element {
  const displayName = getFormattedUsername(props);
  return (
    <>
      <span className={SpaceHeaderStyles.UserLabel}>
        {Messages.user()}
      </span>
      <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.username}>
        {displayName}
      </span>
    </>
  );
}

function getFormattedUsername(props: UserSpaceHeaderProps): string {
  const { item, isServerDomainFamilyActiveDirectory, isSessionServerAdmin, isSessionSiteAdmin } = props;
  const { username, domainName } = item;
  const isTableauCloud = ServerSettingsUnauthenticated.domainFamily() === DomainFamily.External;
  if (isTableauCloud) {
    const authPrefix = getAuthTypePrefix(props);
    return `${authPrefix}\\${username}`;
  }
  const shouldShowDomain = isServerDomainFamilyActiveDirectory || isSessionServerAdmin || isSessionSiteAdmin;
  return shouldShowDomain ? `${domainName}\\${username}` : username;
}

function getAuthTypePrefix(props: UserSpaceHeaderProps): string {
  const siteUserItem = props.item as ISiteUserInfo;
  const authSetting = siteUserItem.authSetting;
  const configName = siteUserItem.idpConfigurationName;
  if (authSetting === SiteUserAuthSetting.Saml) {
    // If we have the config name, otherwise fall back to SAML
    return configName || Messages.ssoAuthTypeSAML_name_undefined();
  }
  if (authSetting === SiteUserAuthSetting.Oidc) {
    // If we have the config name, otherwise fall back to OIDC
    return configName || Messages.ssoAuthTypeOIDC();
  }
  if (authSetting === SiteUserAuthSetting.TabId_with_MFA) {
    return Messages.ssoAuthTypeDefault_name_tableauid_mfa();
  }
  return Messages.ssoAuthTypeDefault_name_tableauid();
}

function SiteRole(props: UserSpaceHeaderProps): JSX.Element {
  if (!_.isUndefined(props.siteRole) && (props.isSessionUserSelf || props.isSessionServerAdmin || props.isSessionSiteAdmin)) {
    const label = props.inServerArea ? Messages.siteUserPlaceMaxSiteRole() : Messages.siteUserPlaceSiteRole();
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {label}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.siterole}>
          {props.siteRoleOptions.getSiteRoleName(props.siteRole)()}
        </span>
      </>
    );
  }

  return null;
}

function lastSignedInText(value: string): string {
  if(value){
    return L10n.localizedDateTime(value);
  } else {
    return Messages.never();
  }
}

function LastSignedIn(props: UserSpaceHeaderProps): JSX.Element {
  if (props.item.lastSignIn && (props.isSessionUserSelf || props.isSessionServerAdmin || props.isSessionSiteAdmin)) {
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {Messages.siteUserPlaceLastSignIn()}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.lastSignedIn}>
          {lastSignedInText(props.item.lastSignIn)}
        </span>
      </>
    );
  }

  return null;
}

function Email(props: UserSpaceHeaderProps): JSX.Element {
  if (props.userSettings && props.userSettings.email) {
    return (
      <>
        <a
          className={SpaceHeaderStyles.userEmailHeaderLink}
          data-tb-test-id={SpaceHeaderTestIds.email}
          href={`mailto:${props.userSettings.email}`}
        >
          {props.userSettings.email}
        </a>
      </>
    );
  }

  return null;
}
